import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { DollarSign, Calendar, Tag, FileText, AlertCircle, Link2 } from 'lucide-react';

const TransactionModal = ({ isOpen, onClose, transaction, events, onSave }) => {
  const [formData, setFormData] = useState({
    type: 'expense',
    amount: '',
    category: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    eventId: '',
    vendor: '',
    reference: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const revenueCategories = [
    'Ticket Sales',
    'Sponsorship',
    'Merchandise',
    'Food & Beverage Commission',
    'Partnership Revenue',
    'Other'
  ];

  const expenseCategories = [
    'DJ Fees',
    'Production',
    'Staffing',
    'Marketing',
    'Operations',
    'Equipment Rental',
    'Venue Costs',
    'Permits & Licenses',
    'Insurance',
    'Transportation',
    'Other'
  ];

  useEffect(() => {
    if (transaction) {
      setFormData({
        type: transaction.type || 'expense',
        amount: transaction.amount || '',
        category: transaction.category || '',
        description: transaction.description || '',
        date: transaction.date || new Date().toISOString().split('T')[0],
        eventId: transaction.eventId || '',
        vendor: transaction.vendor || '',
        reference: transaction.reference || ''
      });
    } else {
      // Reset form for new transaction
      setFormData({
        type: 'expense',
        amount: '',
        category: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        eventId: '',
        vendor: '',
        reference: ''
      });
    }
    setErrors({});
  }, [transaction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset category when type changes
      ...(name === 'type' && { category: '' })
    }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    try {
      await onSave({
        ...formData,
        amount: parseFloat(formData.amount)
      });
      onClose();
    } catch (error) {
      console.error('Error saving transaction:', error);
      setErrors({ submit: error.message || 'Failed to save transaction' });
    } finally {
      setLoading(false);
    }
  };

  const categories = formData.type === 'revenue' ? revenueCategories : expenseCategories;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={transaction ? 'Edit Transaction' : 'Add Transaction'}
      maxWidth="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Transaction Type */}
        <div>
          <label className="block text-sm font-medium mb-2">Transaction Type</label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => handleChange({ target: { name: 'type', value: 'revenue' }})}
              className={`p-4 rounded-lg border transition-all ${
                formData.type === 'revenue'
                  ? 'border-green-500 bg-green-500/10 text-green-500'
                  : 'border-[#333333] hover:border-[#444444]'
              }`}
            >
              <DollarSign className="mx-auto mb-2" size={24} />
              <p className="text-sm font-medium">Revenue</p>
            </button>
            <button
              type="button"
              onClick={() => handleChange({ target: { name: 'type', value: 'expense' }})}
              className={`p-4 rounded-lg border transition-all ${
                formData.type === 'expense'
                  ? 'border-red-500 bg-red-500/10 text-red-500'
                  : 'border-[#333333] hover:border-[#444444]'
              }`}
            >
              <DollarSign className="mx-auto mb-2" size={24} />
              <p className="text-sm font-medium">Expense</p>
            </button>
          </div>
        </div>

        {/* Amount and Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              <DollarSign size={16} className="inline mr-1" />
              Amount (€) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              step="0.01"
              min="0"
              className={`input-dark w-full ${errors.amount ? 'border-red-500' : ''}`}
              placeholder="0.00"
            />
            {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              <Calendar size={16} className="inline mr-1" />
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className={`input-dark w-full ${errors.date ? 'border-red-500' : ''}`}
            />
            {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
          </div>
        </div>

        {/* Category and Event */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              <Tag size={16} className="inline mr-1" />
              Category <span className="text-red-500">*</span>
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className={`input-dark w-full ${errors.category ? 'border-red-500' : ''}`}
            >
              <option value="">Select category</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              <Link2 size={16} className="inline mr-1" />
              Related Event
            </label>
            <select
              name="eventId"
              value={formData.eventId}
              onChange={handleChange}
              className="input-dark w-full"
            >
              <option value="">No event selected</option>
              {events?.map(event => (
                <option key={event.id} value={event.id}>
                  {event.name} {event.date && `(${new Date(event.date).toLocaleDateString()})`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Vendor/Source */}
        {formData.type === 'expense' && (
          <div>
            <label className="block text-sm font-medium mb-2">Vendor/Supplier</label>
            <input
              type="text"
              name="vendor"
              value={formData.vendor}
              onChange={handleChange}
              className="input-dark w-full"
              placeholder="e.g., Pioneer DJ Portugal"
            />
          </div>
        )}

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-2">
            <FileText size={16} className="inline mr-1" />
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className={`input-dark w-full ${errors.description ? 'border-red-500' : ''}`}
            placeholder="Provide details about this transaction..."
          />
          {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
        </div>

        {/* Reference/Invoice Number */}
        <div>
          <label className="block text-sm font-medium mb-2">Reference/Invoice Number</label>
          <input
            type="text"
            name="reference"
            value={formData.reference}
            onChange={handleChange}
            className="input-dark w-full"
            placeholder="e.g., INV-2025-001"
          />
        </div>

        {/* Error Message */}
        {errors.submit && (
          <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2">
            <AlertCircle size={20} className="text-red-500" />
            <p className="text-red-500 text-sm">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-4 border-t border-[#333333]">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`btn-primary ${
              formData.type === 'revenue' 
                ? 'bg-green-500 hover:bg-green-600' 
                : 'bg-red-500 hover:bg-red-600'
            }`}
            disabled={loading}
          >
            {loading ? 'Saving...' : transaction ? 'Update Transaction' : 'Add Transaction'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TransactionModal;